import { trackFlowEnteredAnalytics } from '@util/FlowUtils';
import { useQuery } from '@util/QueryParams';
import { useNervaBackgroundAnimation } from '@util/UseNervaBackgroundAnimation';
import Lottie from 'lottie-react';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { isDefined } from 'ts-utils';
import { hasASLInURL } from '../../config/AssessmentUtils';
import { chooseAssessmentConfig } from '../../config/ChooseAssessmentConfig';
import { Color } from '../../config/Color';
import { ActionType } from '../../store/ActionTypes';
import { useAppDispatch, useAppStore } from '../../store/Store';
import '../../theme.less';
import { PreloadFlowImages } from '../assets/preloadFlowImages/PreloadFlowImages';
import { FlowStack } from '../flows/FlowStack';
import { LoadingSpinner } from '../styles/LoadingSpinner';

export const Onboard: FunctionComponent = () => {
  const purchase = useAppStore((state) => state.purchase);
  const user = useAppStore((state) => state.user);
  const referralState = useAppStore((state) => state.referral);
  const flowConfig = useMemo(
    () => chooseAssessmentConfig({ markExperimentsViewed: true }),
    [referralState.isWearableProvided]
  );
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [backdropColor, setBackdropColor] = useState<undefined | string>(undefined);
  const authToken = useQuery().get('token') ?? undefined;
  const backgroundAnimation = useNervaBackgroundAnimation();

  useEffect(
    function trackAnalyticsEvent() {
      trackFlowEnteredAnalytics(purchase, flowConfig.name);
    },
    [flowConfig.name]
  );

  useEffect(
    function signInWithQueryParamToken() {
      if (authToken !== undefined) {
        dispatch({
          type: ActionType.LOGIN_USER,
          details: { token: authToken },
        });
      }
    },
    [authToken]
  );

  useEffect(
    function redirectIfHasAccessCode() {
      if (hasASLInURL()) return;
      if (referralState.isAslReferral === true) return;
      if (referralState.hasUserId && referralState.hasAccessCode) {
        const currentParams = new URLSearchParams(window.location.search);
        const isLocal = window.location.hostname === 'localhost';
        const downloadAppPath = `${isLocal ? '/nerva' : ''}/youHaveAccess`;
        history.replace(`${downloadAppPath}?${currentParams.toString()}`);
      }
    },
    [referralState.hasUserId, referralState.hasAccessCode, referralState.isAslReferral, history]
  );
  /**
   * We show a loading spinner until the user and referral are fully loaded, because
   * descendants depend on both. Including referralState.loading ensures the access-code
   * redirect to /youHaveAccess has a chance to fire before FlowStack renders.
   */

  const shouldShowLoading = (authToken !== undefined && user.loading) || referralState.loading;

  return (
    <Backdrop color={backdropColor ?? Color.surface.default}>
      {isDefined(backgroundAnimation) && (
        <BackgroundAnimation>
          {backgroundAnimation.type === 'svg' ? (
            <BackgroundSvg src={backgroundAnimation.path} alt="" />
          ) : (
            <Lottie
              animationData={backgroundAnimation.data}
              loop={true}
              autoplay={true}
              rendererSettings={{
                preserveAspectRatio: 'none',
              }}
            />
          )}
        </BackgroundAnimation>
      )}
      <ContentWrapper>
        {shouldShowLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <PreloadFlowImages flowConfig={flowConfig} />
            <FlowStack config={flowConfig} onChangeBackdropColor={setBackdropColor} />
          </>
        )}
      </ContentWrapper>
    </Backdrop>
  );
};

type BackdropProps = {
  color?: string | undefined;
};

const Backdrop = styled.div<BackdropProps>`
  position: relative;
  height: 100%;
  min-height: 100vh;
  background: ${({ color }) => color ?? Color.surface.default};
  transition: 0.3s background-color ease-in-out;
  /* clip rather than hidden: hidden makes this a scroll container, which stops the sticky header
     from sticking. Clipping is visually identical without that side effect. */
  overflow: clip;
`;

const BackgroundAnimation = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  pointer-events: none;

  /* Ensure Lottie fills the container */
  > div {
    width: 100% !important;
    height: 100% !important;
  }

  /* Force SVG to fill and slice (cover behavior) */
  svg {
    width: 100%;
    height: 100%;
    /* Override Lottie's default preserveAspectRatio */
    &[preserveAspectRatio] {
      /* none = stretch to fill, or xMidYMid slice = cover-like behavior */
      /* Using none to ensure full coverage without cropping */
    }
  }
`;

const BackgroundSvg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  min-height: 100vh;
`;
